/**
 * /dashboard/settings/asset-types — Settings → People & HR → Asset types
 * (SOP §8 + §11.3).
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/asset-types/page.tsx.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Package } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useAssetTypes } from "@/hooks/use-hr";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { AssetTypeForm } from "@/components/hr/asset-type-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { EmptyState } from "@/components/shared/empty-state";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/asset-types",
)({
  component: SettingsAssetTypesRoute,
});

function SettingsAssetTypesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useAssetTypes();

  const types = useMemo(
    () =>
      [...(q.data?.asset_types ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [q.data],
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Asset Types</h2>
              <p className="text-sm text-muted-foreground">
                Categories of company asset (Laptop, Phone, etc.).
              </p>
            </div>
            {/* Phones: primary action first, everything full width. */}
            <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:flex-wrap">
              {isHrAdmin && (
                <RestoreDefaultsButton
                  catalog="asset-types"
                  catalogLabel="asset types"
                  invalidateKeys={[["hr", "asset-types"]]}
                />
              )}
              {isHrAdmin && (
                <div className="order-first grid sm:contents">
                  <AssetTypeForm />
                </div>
              )}
            </div>
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change asset types.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3", "s4"].map((k) => (
                <Skeleton key={k} className="h-16 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load asset types</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && types.length === 0 && (
            <EmptyState
              icon={Package}
              title="No Asset Types Yet"
              description={
                isHrAdmin
                  ? "Asset types are the categories of company kit you assign — laptops, phones, access cards. Use Restore defaults above to seed the standard set, or New asset type to add your own."
                  : "Asset types are the categories of company kit you assign — laptops, phones, access cards. An HR manager or company admin can add them."
              }
            />
          )}

          {q.data && types.length > 0 && (
            <div className="space-y-3">
              {types.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base">{t.name}</CardTitle>
                        {t.description && (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {t.description}
                          </p>
                        )}
                      </div>
                      {isHrAdmin && (
                        <div className="shrink-0">
                          <AssetTypeForm existing={t} />
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
